import React from 'react';
import classNames from 'classnames';

import css from './WhenSomethingGoesWrong.module.css';

const DISPUTE_STEPS = [
  'Message',
  'Try to resolve',
  'Report to PageHop',
  'PageHop reviews',
  'Resolution',
];

/**
 * PageHop 101 guidance for order problems: message the other party first where appropriate,
 * escalate through the transaction page, and report safety or fraud concerns straight away.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Additional class for the root section
 * @returns {JSX.Element} The "When something goes wrong" section
 */
const WhenSomethingGoesWrong = ({ className }) => (
  <section
    id="when-something-goes-wrong"
    className={classNames(css.root, className)}
    aria-labelledby="when-something-goes-wrong-heading"
  >
    <h2 id="when-something-goes-wrong-heading">When something goes wrong</h2>

    <h3>Start with a conversation</h3>
    <p>
      If there’s a problem with an order, message the buyer or seller through PageHop first. Be
      clear about what went wrong and what resolution you’re looking for. Keep the conversation in
      PageHop messages so there’s a record if we need to step in.
    </p>

    <h3>Give them a chance to respond</h3>
    <p>
      Many issues — such as shipping delays, condition concerns, missing details, or
      misunderstandings — can often be resolved directly.
    </p>

    <h3>Still not resolved?</h3>
    <p>
      If you can’t resolve the issue together, report it through the transaction page. PageHop can
      review the transaction, messages, listing information and other relevant details before
      deciding what happens next.
    </p>

    <h3>Safety or fraud concerns?</h3>
    <p>
      You do not need to contact the other person first if the issue involves harassment, threats,
      fraud, counterfeit goods, suspected scams, or another safety concern. Report it to PageHop
      right away.
    </p>

    <h3>How disputes work</h3>
    <ol className={css.flow} aria-label="Dispute process">
      {DISPUTE_STEPS.map(step => (
        <li key={step}>{step}</li>
      ))}
    </ol>
    <p>
      Depending on the transaction and what PageHop finds, a dispute may be resolved by refunding
      the buyer or by releasing payment to the seller. There’s no automatic outcome: PageHop decides
      after reviewing the details.
    </p>

    <h3>After an issue is reported</h3>
    <ul className={css.afterReport}>
      <li>The order is marked as under review and payment stays on hold until it’s resolved.</li>
      <li>
        PageHop reviews the transaction, listing information, PageHop messages and other relevant
        details. We may contact the buyer or seller for more information.
      </li>
      <li>
        Reporting an issue does not automatically result in a refund. Depending on the outcome, the
        buyer may be refunded or payment may be released to the seller.
      </li>
      <li>
        Both of you can keep messaging in PageHop while the review is open. Keep any photos,
        shipping information or other evidence until the issue is resolved.
      </li>
      <li>We aim to acknowledge reported order issues within one business day.</li>
    </ul>

    <h3>Order problem or general concern?</h3>
    <p>
      For a problem with a specific order, use Report an issue on the transaction page. For general
      safety, account, listing, or behaviour concerns, use Report a concern.
    </p>

    <h3>Keep it on PageHop</h3>
    <p>
      Keep order-related communication in PageHop messages. It protects both buyers and sellers and
      gives PageHop a record if we need to step in. Don’t move order conversations or payments off
      PageHop.
    </p>
  </section>
);

export default WhenSomethingGoesWrong;
